import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, UserPlus, Eye, EyeOff, Shield, MapPin, Activity, Bell, Lightbulb, Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

interface FamilySharingWidgetProps {
    color?: string;
}

const FamilySharingWidget: React.FC<FamilySharingWidgetProps> = ({ color = "from-indigo-400 to-blue-600" }) => {
    const { t } = useTranslation("nonHealth");
    const [expanded, setExpanded] = useState<number | null>(null);
    const [members, setMembers] = useState([
        { name: "mom", avatar: "م", color: "bg-rose-400", permissions: ["location", "health", "alert"], online: true },
        { name: "dad", avatar: "پ", color: "bg-blue-500", permissions: ["location", "health", "alert"], online: true },
        { name: "aunt", avatar: "ع", color: "bg-violet-400", permissions: ["location"], online: false },
    ]);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setMembers(prev => prev.map(m => Math.random() > 0.7 ? { ...m, online: !m.online } : m));
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <motion.div
            key="familySharing-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
        >
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 shadow-xl border border-indigo-200 relative overflow-hidden">
                {/* افکت پس‌زمینه */}
                <motion.div
                    className="absolute -left-10 top-10 w-40 h-40 bg-gradient-to-br from-indigo-400/15 to-blue-600/10 rounded-full blur-2xl"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4] }}
                    transition={{ duration: 8, repeat: Infinity }}
                />

                {/* هدر */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <motion.div
                            className={`w-9 h-9 rounded-full bg-gradient-to-r ${color} flex items-center justify-center text-white shadow-md`}
                            animate={{ scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            <Users className="w-5 h-5" />
                        </motion.div>
                        <h5 className="text-indigo-800 font-semibold text-lg">{t("widgets.familySharing.title")}</h5>
                    </div>
                    <motion.div className={`bg-gradient-to-r ${color} text-white px-3 py-1 rounded-full text-xs font-medium shadow-sm`}>{t("widgets.familySharing.activeMembers", { count: members.length })}</motion.div>
                </div>

                {/* نمایش اعضا */}
                <div className="space-y-2 mb-4">
                    {members.map((member, idx) => (
                        <motion.div
                            key={idx}
                            className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * idx }}
                        >
                            <motion.button
                                className="w-full flex items-center justify-between p-3"
                                onClick={() => setExpanded(expanded === idx ? null : idx)}
                                whileHover={{ backgroundColor: "rgba(255,255,255,0.8)" }}
                            >
                                <div className="flex items-center gap-2.5">
                                    <div className="relative">
                                        <div className={`w-9 h-9 ${member.color} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm`}>{member.avatar}</div>
                                        <motion.div
                                            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${member.online ? "bg-green-500" : "bg-gray-400"}`}
                                            animate={member.online ? { scale: [1, 1.2, 1] } : {}}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        />
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-semibold text-gray-800">{t(`widgets.familySharing.${member.name}`)}</p>
                                        <p className="text-xs text-gray-500">
                                            {member.permissions.includes("health") ? t("widgets.familySharing.fullAccess") : t("widgets.familySharing.locationOnly")}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1">
                                        {member.permissions.includes("location") && <MapPin className="w-3.5 h-3.5 text-sky-500" />}
                                        {member.permissions.includes("health") && <Activity className="w-3.5 h-3.5 text-rose-500" />}
                                        {member.permissions.includes("alert") && <Bell className="w-3.5 h-3.5 text-amber-500" />}
                                    </div>
                                    <motion.div animate={{ rotate: expanded === idx ? 180 : 0 }} transition={{ duration: 0.3 }}>
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                    </motion.div>
                                </div>
                            </motion.button>

                            <AnimatePresence>
                                {expanded === idx && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="border-t border-gray-100 px-3 pb-3"
                                    >
                                        <p className="text-xs text-gray-500 mt-2 mb-1.5">{t("widgets.familySharing.permissionsLabel")}</p>
                                        <div className="grid grid-cols-3 gap-1.5">
                                            {[
                                                { label: t("widgets.familySharing.location"), icon: <MapPin className="w-3.5 h-3.5" />, key: "location" },
                                                { label: t("widgets.familySharing.health"), icon: <Activity className="w-3.5 h-3.5" />, key: "health" },
                                                { label: t("widgets.familySharing.alert"), icon: <Bell className="w-3.5 h-3.5" />, key: "alert" },
                                            ].map((perm) => (
                                                <motion.div
                                                    key={perm.key}
                                                    className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium ${
                                                        member.permissions.includes(perm.key)
                                                            ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                                                            : "bg-gray-100 text-gray-400 border border-gray-200"
                                                    }`}
                                                    whileHover={{ scale: 1.05 }}
                                                >
                                                    {perm.icon}
                                                    {perm.label}
                                                    {member.permissions.includes(perm.key) && <Check className="w-3 h-3 mr-auto" />}
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>

                {/* دکمه افزودن عضو */}
                <motion.button
                    className={`w-full bg-gradient-to-r ${color} text-white py-2.5 rounded-xl text-sm font-semibold shadow-md relative overflow-hidden mb-3`}
                    whileHover={{ scale: 1.02, y: -2, boxShadow: "0 8px 20px rgba(99, 102, 241, 0.3)" }}
                    whileTap={{ scale: 0.98 }}
                >
                    <motion.span
                        className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                        initial={{ x: "-100%" }}
                        animate={{ x: "100%" }}
                        transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-2">
                        <UserPlus className="w-4 h-4" />
                        {t("widgets.familySharing.addMember")}
                    </span>
                </motion.button>

                {/* پیام راهنما */}
                <motion.div
                    className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-indigo-200 text-indigo-700 text-xs p-2 rounded-lg flex items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                >
                    <motion.span
                        className="ml-2 bg-indigo-100 w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                        animate={{ rotate: [0, -10, 10, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <Shield className="w-3.5 h-3.5" />
                    </motion.span>
                    <span>{t("widgets.familySharing.helpText")}</span>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default FamilySharingWidget;
